import React, { useRef, useLayoutEffect, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Draggable from 'react-draggable';
import Icon from '../Icon';

export default function DraggableModal({
  open = false,
  onClose,
  title = '',
  children,
  initialPosition = { x: 0, y: 0 },
  width = 760,
  height,
  zIndex = 1050,
  closeOnOutsideClick = false,
}) {
  const nodeRef = useRef(null);

  useEffect(() => {
    if (!open || !closeOnOutsideClick) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose, closeOnOutsideClick]);

  const [defaultPos, setDefaultPos] = useState(initialPosition);

  useLayoutEffect(() => {
    const margin = 2;
    const viewportW = document.documentElement.clientWidth;
    const modalW =
      typeof width === 'number' ? width : nodeRef.current?.offsetWidth || 0;
    const x = Math.max(0, viewportW - modalW - margin);
    setDefaultPos({ x, y: initialPosition?.y ?? 0 });
  }, [open, width, initialPosition?.y]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: '0',
        zIndex: 1000,
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <Draggable
        key={`${defaultPos.x}-${defaultPos.y}`}
        handle=".drag-handle"
        nodeRef={nodeRef}
        defaultPosition={defaultPos}
        bounds="body"
      >
        <div
          ref={nodeRef}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            zIndex,
            width,
            background: '#fff',
            borderRadius: 8,
            boxShadow: '0 12px 28px rgba(0,0,0,0.2)',
            border: '1px solid rgba(0,0,0,0.06)',
          }}
        >
          <div
            className="drag-handle"
            style={{
              cursor: 'move',
              userSelect: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '8px 12px',
              borderBottom: '1px solid #eee',
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8,
              background: '#fafafa',
            }}
          >
            <span style={{ fontWeight: 600 }}>{title}</span>
            <Icon onClick={onClose} style={{ cursor: 'pointer' }}>
              close
            </Icon>
          </div>

          <div
            style={{
              maxHeight: height || '80vh',
              minHeight: height || '80vh',
              overflow: 'auto',
              padding: 12,
            }}
          >
            {children}
          </div>
        </div>
      </Draggable>
    </div>,
    document.body,
  );
}
